import React from 'react';

import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import type { ExtensionComponentProps, Parameters } from '@atlaskit/editor-common/extensions';
import Heading from '@atlaskit/heading';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export interface NativeEmbedFallbackParams extends Parameters {
	url?: string;
}

type NativeEmbedFallbackProps = ExtensionComponentProps<NativeEmbedFallbackParams> & {
	onConvert: () => void;
};

const messages = defineMessages({
	title: {
		id: 'native-embeds-fallback-editor-extension.title',
		defaultMessage: 'Embed failed to load.',
		description:
			'Title shown in fallback extension when embedded content cannot be rendered in an editor.',
	},
	description: {
		id: 'native-embeds-fallback-editor-extension.description',
		defaultMessage: 'Something went wrong while loading the content.',
		description: 'Body text shown in fallback extension for failed embedded content in an editor.',
	},
	refresh: {
		id: 'native-embeds-fallback-editor-extension.refresh',
		defaultMessage: 'Refresh',
		description: 'Label for button that retries loading embedded content in an editor.',
	},
});

const styles = cssMap({
	container: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
		minHeight: '120px',
		backgroundColor: token('elevation.surface'),
	},
});

export const NativeEmbedsFallbackExtension = ({
	onConvert,
}: NativeEmbedFallbackProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box testId="native-embeds-fallback-extension" xcss={styles.container}>
			<Stack space="space.100" alignInline="center">
				<Heading size="small">{formatMessage(messages.title)}</Heading>
				<Text color="color.text.subtle">{formatMessage(messages.description)}</Text>
				<Button iconBefore={RefreshIcon} onClick={onConvert}>
					{formatMessage(messages.refresh)}
				</Button>
			</Stack>
		</Box>
	);
};
